import {
	createCanvasTimelineDate,
	getMomentFromCanvasTimelineDate,
	getPersonGroups,
	ITEM_TYPE,
} from '../canvas-timeline/canvas_timeline_util';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import TaskItem from './items/task_item';
import NonProjectTimeGroup from './groups/non_project_time_group';
import PlaceholderGroup from './groups/placeholder_group';
import PersonGroup from './groups/person_group';
import {dispatch, EVENT_ID} from '../../../containers/event_manager';
import {ProjectAllocationItem} from './items/project_allocation_item';
import {hasPermission} from '../../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import {removeFromArray} from "../utils";

export const handlePeopleSchedulingMutation = (pageComponent, response) => {
	const responseMutation = response?.createAllocation || response?.updateAllocation || response?.deleteAllocation;
	if (!response || responseMutation?.errors) return null;

	const data = pageComponent.getData();
	if (!data) {
		pageComponent.mutationResponse = response;
		pageComponent.setState({savingMutation: true});
		return;
	}
	pageComponent.mutationResponse = undefined;
	const {formatMessage} = pageComponent.props.intl;
	const {items, todayDate} = pageComponent.state;

	function createOrUpdateAllocation(responseAllocation) {
		const formattedAllocation = {
			id: responseAllocation.id,
			description: responseAllocation.description,
			monday: responseAllocation.monday,
			tuesday: responseAllocation.tuesday,
			wednesday: responseAllocation.wednesday,
			thursday: responseAllocation.thursday,
			friday: responseAllocation.friday,
			saturday: responseAllocation.saturday,
			sunday: responseAllocation.sunday,
			startYear: responseAllocation.startYear,
			startMonth: responseAllocation.startMonth,
			startDay: responseAllocation.startDay,
			endYear: responseAllocation.endYear,
			endMonth: responseAllocation.endMonth,
			endDay: responseAllocation.endDay,
			personId: responseAllocation.person?.id,
			projectId: responseAllocation.project?.id,
			projectGroupId: responseAllocation.projectGroupId,
			projectGroupColor: responseAllocation.projectGroupColor,
			idleTimeId: responseAllocation.idleTime?.id,
			isIdleTimeInternal: responseAllocation.idleTime?.isInternalTime,
			idleTimeName: responseAllocation.idleTime?.name,
			registerTime: responseAllocation.registerTime,
		};
		const person = data.persons.find(person => person.id === formattedAllocation.personId);
		const {projectId, projectGroupId} = formattedAllocation;

		if (!formattedAllocation.idleTimeId) {
			formattedAllocation.displayName = projectId
				? data.projects.find(project => project.id === projectId).name
				: projectGroupId
				? data.projectGroups.find(projectGroup => projectGroup.id === projectGroupId).name
				: null;
			const personGroup = getPersonGroups(pageComponent.state.groups).find(
				group => group.id === formattedAllocation.personId
			);
			const allocationGroup = personGroup.groups.find(
				group => group.id === `${formattedAllocation.personId}-${projectGroupId || projectId}`
			);
			if (!allocationGroup) {
				const projectOrProjectGroup = projectGroupId
					? data.projectGroups.find(projectGroup => projectGroup.id === projectGroupId)
					: data.projects.find(project => project.id === projectId);

				const {personId} = formattedAllocation;
				const person = data.persons.find(person => person.id === personId);
				const newAllocationProjectId = projectId
					? projectId
					: projectGroupId
					? data.projects.find(p => p.projectGroupId === projectGroupId).id
					: null;

				// if the person is not in the team of the project add him to be able to draw the allocation
				if (
					newAllocationProjectId &&
					!data.projectPersons.find(
						projectPerson =>
							projectPerson.personId === person.id && projectPerson.projectId === newAllocationProjectId
					)
				) {
					const newProjectPerson = {
						personId: person.id,
						projectId: newAllocationProjectId,
					};

					data.projectPersons.push(newProjectPerson);
				}

				// Create a UI ProjectGroup for the project or connected project
				const newProjectGroup = pageComponent.getNewProjectGroup(personGroup.data, projectOrProjectGroup);
				personGroup.groups.push(newProjectGroup);
			}
		}

		const changedAllocation = data.allocations.find(allocation => allocation.id === responseAllocation.id);

		if (changedAllocation) {
			data.allocations.splice(data.allocations.indexOf(changedAllocation), 1, formattedAllocation);
			const allocationItem = items.find(
				item => item.data.allocation && item.data.allocation.id === responseAllocation.id
			);
			allocationItem.resetItemRow();
			allocationItem.updateWithAllocation(formattedAllocation);
		} else {
			data.allocations.push(formattedAllocation);

			const projectAllocationItem = new ProjectAllocationItem(pageComponent, formattedAllocation);
			if (projectAllocationItem.isValid()) {
				pageComponent.state.items.push(projectAllocationItem);
			}
		}
		if (pageComponent.heatmapCache.get(person.id)) {
			pageComponent.heatmapCache.get(person.id).needsRecalculation = true;
		}
	}

	function deleteAllocation(allocationToDelete) {
		const deletedAllocationId = allocationToDelete.id;
		data.allocations.splice(data.allocations.indexOf(allocationToDelete), 1);

		const itemIndex = items.findIndex(item => item.data.allocation && item.data.allocation.id === deletedAllocationId);
		items.splice(itemIndex, 1);

		const personId = allocationToDelete.personId;
		if (pageComponent.heatmapCache.get(personId)) {
			pageComponent.heatmapCache.get(personId).needsRecalculation = true;
		}
	}

	function createProjectPerson(projectOrProjectGroupId, projectPerson) {
		const personId = projectPerson.person.id;
		const projectId = projectPerson.project.id;
		const person = data.persons.find(person => person.id === personId);
		const newProjectPerson = {
			id: projectPerson.id,
			personId: person.id,
			roleId: person.roleId,
			projectId: projectId,
		};

		const alreadyExists = data.projectPersons.some(pp => pp.personId === person.id && pp.projectId === projectId);

		if (!alreadyExists) {
			data.projectPersons.push(newProjectPerson);
			const projectPersonsOnProject = data.projectPersonByProjectMap[projectId] || [];
			data.projectPersonByProjectMap[projectId] = projectPersonsOnProject;
			projectPersonsOnProject.push(newProjectPerson);
			const projectPersonsForPerson = data.projectPersonByPersonMap[person.id] || [];
			data.projectPersonByPersonMap[person.id] = projectPersonsForPerson;
			projectPersonsForPerson.push(newProjectPerson);
		}

		addProjectGroup(personId, projectOrProjectGroupId);
	}

	function deleteProjectPerson(projectId, projectPerson) {
		const projectPersonId = projectPerson.id;
		const personId = projectPerson.personId;
		removeFromArray(data.projectPersons, projectPerson => projectPerson.id === projectPersonId);
		removeFromArray(data.projectPersonByProjectMap[projectId], projectPerson => projectPerson.id === projectPersonId);
		removeFromArray(data.projectPersonByPersonMap[personId], projectPerson => projectPerson.id === projectPersonId);

		const project = data.projects.find(project => project.id === projectId);
		const personGroup = getPersonGroups(pageComponent.state.groups).find(group => group.id === personId);
		removeFromArray(personGroup?.groups, group => group.id === `${personId}-${project.projectGroupId || project.id}`)

		if (pageComponent.heatmapCache.get(personId)) {
			pageComponent.heatmapCache.get(personId).needsRecalculation = true;
		}
		pageComponent.clearCompanyUtilizationCache();
	}

	function addProjectGroup(personId, projectOrGroupProjectId) {
		const personGroup = getPersonGroups(pageComponent.state.groups).find(group => group.id === personId);
		if (!personGroup) {
			// E.g. client users, etc.
			return;
		}
		let projectOrProjectGroup =
			data.projects.find(project => project.id === projectOrGroupProjectId) || data.projectGroups.find(pg => pg.id === projectOrGroupProjectId);
		if (projectOrProjectGroup.projectGroupId) {
			projectOrProjectGroup = data.projectGroups.find(pg => pg.id === projectOrProjectGroup.projectGroupId);
		}

		const allocationGroup = personGroup.groups.find(
			group => group.id === `${personId}-${projectOrProjectGroup.id}`
		);
		if (!allocationGroup) {
			// Create a UI ProjectGroup for the project or connected project
			const newProjectGroup = pageComponent.getNewProjectGroup(personGroup.data, projectOrProjectGroup);
			personGroup.groups.push(newProjectGroup);
			if (pageComponent.heatmapCache.get(personId)) {
				pageComponent.heatmapCache.get(personId).needsRecalculation = true;
			}
		}
	}

	function updateTaskAssignees(updatedTask) {
		if (pageComponent.heatmapCache.get(updatedTask.roleId)) {
			pageComponent.heatmapCache.get(updatedTask.roleId).needsRecalculation = true;
		}
		if (updatedTask.assignedPersons.length) {
			pageComponent.clearCompanyUtilizationCache();
			for (const personId of updatedTask.assignedPersons) {
				if (pageComponent.heatmapCache.get(personId)) {
					pageComponent.heatmapCache.get(personId).needsRecalculation = true;
				}
				const taskItemData = pageComponent.composeTaskItem(updatedTask, personId, todayDate);
				if (!taskItemData) continue;
				const taskItem = new TaskItem(taskItemData);
				items.push(taskItem);
				addProjectGroup(personId, updatedTask.projectId);
			}
		} else {
			const taskItemData = pageComponent.composeTaskItem(updatedTask, null, todayDate);
			if (taskItemData) {
				const taskItem = new TaskItem(taskItemData);
				items.push(taskItem);
			}
		}
	}

	function deleteTaskItems(taskId) {
		//Delete the task from all assigned people and unassigned section
		const getTaskItemIndex = () => {
			return items.findIndex(
				item => item.itemType === ITEM_TYPE.PEOPLE_SCHEDULING_TASK && item.data.task.id === taskId
			);
		};

		let itemIndex = getTaskItemIndex();
		while (itemIndex > -1) {
			items.splice(itemIndex, 1);
			itemIndex = getTaskItemIndex();
		}
	}

	if (response.createAllocation || response.updateAllocation) {
		const responseAllocation = response.updateAllocation
			? response.updateAllocation.allocation
			: response.createAllocation.allocation.node;
		const payload = response.updateAllocation || response.createAllocation;
		const projectOrGroupId = payload.project?.id || payload.projectGroup?.id;
		const projectPersons =
			payload.project?.projectPersons.edges ||
			payload.projectGroup?.projects?.edges.map(edge => edge.node.projectPersons.edges).flat() ||
			[];
		for (const projectPerson of projectPersons) {
			createProjectPerson(projectOrGroupId, projectPerson.node);
		}
		createOrUpdateAllocation(responseAllocation);
		pageComponent.clearCompanyUtilizationCache();
	} else if (response.deleteAllocation) {
		const allocationToDelete = data.allocations.find(
			allocation => allocation.id === response.deleteAllocation.deletedAllocationId
		);
		deleteAllocation(allocationToDelete);
		pageComponent.clearCompanyUtilizationCache();
	} else if (response.createProjectPerson) {
		const projectId = response.createProjectPerson.project?.id || response.createProjectPerson.projectGroup?.id;
		if (response.createProjectPerson.projectPersons) {
			for (const projectPerson of response.createProjectPerson.projectPersons) {
				createProjectPerson(projectId, projectPerson);
			}
		}
	} else if (response.deleteProjectPerson) {
		const deletedProjectPersonIds = response.deleteProjectPerson.deletedProjectPersonIds;
		const deletedProjectPersons = data.projectPersons.filter(projectPerson => deletedProjectPersonIds.includes(projectPerson.id));
		for (const deletedProjectPerson of deletedProjectPersons) {
			// In scheduling, for connected projects, allocations are stored on projectGroup instead of project (which is how it is in service-company).
			const projectGroupId = response.deleteProjectPerson.projectGroup?.id ;
			const allocationsToDelete = data.allocations.filter(
				allocation =>
					allocation.personId === deletedProjectPerson.personId &&
					(allocation.projectId === deletedProjectPerson.projectId || (projectGroupId && allocation.projectGroupId === projectGroupId))
			);
			for (const allocationToDelete of allocationsToDelete) {
				deleteAllocation(allocationToDelete);
			}

			const tasksToUnassign = data.tasks.filter(
				task => task.projectId === deletedProjectPerson.projectId && task.assignedPersons.includes(deletedProjectPerson.personId)
			);
			for (const task of tasksToUnassign) {
				task.assignedPersons = task.assignedPersons.filter(assignee => assignee !== deletedProjectPerson.personId);
				deleteTaskItems(task.id);
				updateTaskAssignees(task);
			}
			
			deleteProjectPerson(deletedProjectPerson.projectId, deletedProjectPerson);
		}
		pageComponent.clearCompanyUtilizationCache();
	} else if (response.updateTask || response.createTask || response.duplicateTask) {
		const responseTask =
			response.updateTask && response.updateTask.tasks
				? response.updateTask.tasks[0]
				: response.updateTask && response.updateTask.taskEdge
				? response.updateTask.taskEdge.node
				: response.duplicateTask
				? response.duplicateTask.task.node
				: response.createTask.task.node;
		const updatedTask = {
			approved: true,
			assignedPersons: responseTask.assignedPersons.map(assignedPerson => {
				return assignedPerson.id;
			}),
			followers: responseTask.followers
				? responseTask.followers.map(followerPerson => {
						return followerPerson.id;
				  })
				: [],
			billable: responseTask.billable,
			blocked: responseTask.blocked,
			bug: responseTask.bug,
			companyTaskId: responseTask.companyTaskId,
			deadlineDay: responseTask.deadlineDay,
			deadlineMonth: responseTask.deadlineMonth,
			deadlineYear: responseTask.deadlineYear,
			deadlineFrom: responseTask.deadlineFrom,
			startFrom: responseTask.startFrom,
			done: responseTask.done,
			estimateForecast: responseTask.estimateForecast,
			highPriority: responseTask.highPriority,
			id: responseTask.id,
			taskLabels: responseTask.taskLabels?.map(t => ({labelId: t.label.id})),
			projectId: responseTask.project.id,
			roleId: responseTask.role ? responseTask.role.id : undefined,
			phaseId: responseTask.phase ? responseTask.phase.id : undefined,
			startDay: responseTask.startDay,
			startMonth: responseTask.startMonth,
			startYear: responseTask.startYear,
			timeLeft: responseTask.timeLeft,
			statusColumnId: responseTask.statusColumnV2.id,
			name: responseTask.name,
			ownerId: responseTask.owner ? responseTask.owner.id : undefined,
			fullAccessToProject: true,
			userCanDeleteTask: responseTask.userCanDeleteTask,
			userCantDeleteTaskReason: responseTask.userCantDeleteTaskReason,
		};
		if (response.updateTask) {
			const changedTask = data.taskMap.get(responseTask.id);
			data.tasks.splice(data.tasks.indexOf(changedTask), 1, updatedTask);
			data.taskMap.set(updatedTask.id, updatedTask);

			// Find one of the old task items to get assigned person before the update
			const item = items.find(
				item => item.itemType === ITEM_TYPE.PEOPLE_SCHEDULING_TASK && item.data.task.id === responseTask.id
			);
			if (item && item.data && item.data.task && item.data.task.assignedPersons) {
				const oldAssigned = item.data.task.assignedPersons;
				// Delete the heatmap cache for the old assigned persons that are no longer in assignedPersons
				for (const oldAssignedId of oldAssigned) {
					if (!updatedTask.assignedPersons.includes(oldAssignedId)) {
						if (pageComponent.heatmapCache.get(oldAssignedId)) {
							pageComponent.heatmapCache.get(oldAssignedId).needsRecalculation = true;
						}
					}
				}
			}
			deleteTaskItems(responseTask.id);
		} else {
			data.tasks.push(updatedTask);
			data.taskMap.set(updatedTask.id, updatedTask);
		}
		if (responseTask.project.projectPersons?.edges) {
			for (const projectPerson of responseTask.project?.projectPersons?.edges) {
				createProjectPerson(responseTask.project.id, projectPerson.node)
			}
		}
		updateTaskAssignees(updatedTask);
	} else if (response.deleteTask) {
		const taskToDelete = data.taskMap.get(response.deleteTask.deletedTasksIds[0]);
		taskToDelete.assignedPersons.forEach(person => {
			if (pageComponent.heatmapCache.get(person)) {
				pageComponent.heatmapCache.get(person).needsRecalculation = true;
			}
		});
		data.tasks.splice(data.tasks.indexOf(taskToDelete), 1);
		if (pageComponent.heatmapCache.get(response.deleteTask.deletedTasksIds[0])) {
			data.taskMap.get(response.deleteTask.deletedTasksIds[0]).needsRecalculation = true;
		}

		deleteTaskItems(response.deleteTask.deletedTasksIds[0]);
	} else if (response.createTimeReg || response.updateTimeReg) {
		const responseTimeRegistration = response.updateTimeReg
			? response.updateTimeReg.timeReg
			: response.createTimeReg.timeReg.node;
		const updatedTimeRegistration = {
			id: responseTimeRegistration.id,
			day: responseTimeRegistration.day,
			month: responseTimeRegistration.month,
			year: responseTimeRegistration.year,
			minutesRegistered: responseTimeRegistration.minutesRegistered,
			personId: responseTimeRegistration.person.id,
			taskId: responseTimeRegistration.task && responseTimeRegistration.task.id,
			projectId: responseTimeRegistration.project && responseTimeRegistration.project.id,
			idleTimeId: responseTimeRegistration.idleTime && responseTimeRegistration.idleTime.id,
			isIdleTimeInternal: responseTimeRegistration.idleTime ? responseTimeRegistration.idleTime.isInternalTime : null,
		};

		if (response.updateTimeReg) {
			const changedTimeRegistration = data.timeRegistrations.find(
				timeRegistration => timeRegistration.id === responseTimeRegistration.id
			);
			data.timeRegistrations.splice(data.timeRegistrations.indexOf(changedTimeRegistration), 1, updatedTimeRegistration);

			const parentId =
				changedTimeRegistration.taskId || changedTimeRegistration.projectId || changedTimeRegistration.idleTimeId;
			const parentTimeRegistrationArray = data.parentTimeRegistrationMap.get(parentId) || [];
			const indexToDelete = parentTimeRegistrationArray.indexOf(changedTimeRegistration);
			if (indexToDelete >= 0) {
				parentTimeRegistrationArray.splice(indexToDelete, 1);
			}
			parentTimeRegistrationArray.push(updatedTimeRegistration);
			data.parentTimeRegistrationMap.set(parentId, parentTimeRegistrationArray);

			const timeRegistrationItem = items.find(
				item =>
					item.itemType === ITEM_TYPE.PEOPLE_SCHEDULING_TIME_REGISTRATION &&
					item.data.timeRegistrationId === responseTimeRegistration.id
			);
			if (timeRegistrationItem) {
				const timeRegistrationDate = createCanvasTimelineDate(
					updatedTimeRegistration.year,
					updatedTimeRegistration.month,
					updatedTimeRegistration.day
				);
				timeRegistrationItem.startDate = timeRegistrationDate;
				timeRegistrationItem.endDate = timeRegistrationDate;
				timeRegistrationItem.minutesRegistered = updatedTimeRegistration.minutesRegistered;
			}

			if (updatedTimeRegistration.taskId) {
				const changedTask = data.taskMap.get(updatedTimeRegistration.taskId);
				changedTask.timeLeft = responseTimeRegistration.task.timeLeft;

				//Delete the task from all assigned people and unassigned section
				const getTaskItemIndex = () => {
					return items.findIndex(
						item =>
							item.itemType === ITEM_TYPE.PEOPLE_SCHEDULING_TASK &&
							item.data.task.id === updatedTimeRegistration.taskId
					);
				};

				let itemIndex = getTaskItemIndex();
				while (itemIndex > -1) {
					items.splice(itemIndex, 1);
					itemIndex = getTaskItemIndex();
				}

				if (pageComponent.heatmapCache.get(changedTask.roleId)) {
					pageComponent.heatmapCache.get(changedTask.roleId).needsRecalculation = true;
				}
				if (changedTask.assignedPersons.length) {
					pageComponent.clearCompanyUtilizationCache();
					for (const personId of changedTask.assignedPersons) {
						if (pageComponent.heatmapCache.get(personId)) {
							pageComponent.heatmapCache.get(personId).needsRecalculation = true;
						}
						const taskItemData = pageComponent.composeTaskItem(changedTask, personId, todayDate);
						if (!taskItemData) continue;
						const taskItem = new TaskItem(taskItemData);
						items.push(taskItem);
					}
				} else {
					const taskItemData = pageComponent.composeTaskItem(changedTask, null, todayDate);
					if (taskItemData) {
						const taskItem = new TaskItem(taskItemData);
						items.push(taskItem);
					}
				}
			}
		} else {
			data.timeRegistrations.push(updatedTimeRegistration);

			const parentId =
				updatedTimeRegistration.taskId || updatedTimeRegistration.projectId || updatedTimeRegistration.idleTimeId;
			const parentTimeRegistrationArray = data.parentTimeRegistrationMap.get(parentId) || [];
			parentTimeRegistrationArray.push(updatedTimeRegistration);
			data.parentTimeRegistrationMap.set(parentId, parentTimeRegistrationArray);

			let changedTask;

			if (updatedTimeRegistration.taskId) {
				changedTask = data.taskMap.get(updatedTimeRegistration.taskId);
				changedTask.timeLeft = responseTimeRegistration.task.timeLeft;
			}

			if (updatedTimeRegistration.taskId) {
				const getTaskItemIndex = () => {
					return items.findIndex(
						item =>
							item.itemType === ITEM_TYPE.PEOPLE_SCHEDULING_TASK &&
							item.data.task.id === updatedTimeRegistration.taskId
					);
				};

				let itemIndex = getTaskItemIndex();
				while (itemIndex > -1) {
					items.splice(itemIndex, 1);
					itemIndex = getTaskItemIndex();
				}

				if (pageComponent.heatmapCache.get(changedTask.roleId)) {
					pageComponent.heatmapCache.get(changedTask.roleId).needsRecalculation = true;
				}
				if (changedTask.assignedPersons.length) {
					pageComponent.clearCompanyUtilizationCache();
					for (const personId of changedTask.assignedPersons) {
						if (pageComponent.heatmapCache.get(personId)) {
							pageComponent.heatmapCache.get(personId).needsRecalculation = true;
						}
						const taskItemData = pageComponent.composeTaskItem(changedTask, personId, todayDate);
						if (!taskItemData) continue;
						const taskItem = new TaskItem(taskItemData);
						items.push(taskItem);
					}
				} else {
					const taskItemData = pageComponent.composeTaskItem(changedTask, null, todayDate);
					if (taskItemData) {
						const taskItem = new TaskItem(taskItemData);
						items.push(taskItem);
					}
				}
			} else {
				if (pageComponent.heatmapCache.get(updatedTimeRegistration.personId)) {
					pageComponent.heatmapCache.get(updatedTimeRegistration.personId).needsRecalculation = true;
				}
			}
		}
	} else if (response.deleteTimeReg) {
		const timeRegistrationToDelete = data.timeRegistrations.find(
			timeRegistration => timeRegistration.id === response.deleteTimeReg.deletedTimeRegId
		);
		data.timeRegistrations.splice(data.timeRegistrations.indexOf(timeRegistrationToDelete), 1);

		const parentId =
			timeRegistrationToDelete.taskId || timeRegistrationToDelete.projectId || timeRegistrationToDelete.idleTimeId;
		const parentTimeRegistrationArray = data.parentTimeRegistrationMap.get(parentId) || [];
		const indexToDelete = parentTimeRegistrationArray.indexOf(timeRegistrationToDelete);
		if (indexToDelete >= 0) {
			parentTimeRegistrationArray.splice(indexToDelete, 1);
		}
		data.parentTimeRegistrationMap.set(parentId, parentTimeRegistrationArray);

		const itemIndex = items.findIndex(
			item =>
				item.itemType === ITEM_TYPE.PEOPLE_SCHEDULING_TIME_REGISTRATION &&
				item.data.timeRegistrationId === response.deleteTimeReg.deletedTimeRegId
		);
		if (itemIndex > -1) {
			items.splice(itemIndex, 1);
		}

		if (timeRegistrationToDelete.taskId) {
			const changedTask = data.taskMap.get(timeRegistrationToDelete.taskId);
			changedTask.timeLeft = response.deleteTimeReg.task.timeLeft;

			const taskTimeRegistrations = data.parentTimeRegistrationMap.get(changedTask.id);
			const timeRegistrationIndex = taskTimeRegistrations.findIndex(
				timeRegistration => timeRegistration.id === response.deleteTimeReg.deletedTimeRegId
			);
			if (timeRegistrationIndex >= 0) {
				taskTimeRegistrations.splice(timeRegistrationIndex, 1);
			}

			const getTaskItemIndex = () => {
				return items.findIndex(
					item => item.itemType === ITEM_TYPE.PEOPLE_SCHEDULING_TASK && item.data.task.id === changedTask.id
				);
			};

			let taskItemIndex = getTaskItemIndex();
			while (taskItemIndex > -1) {
				items.splice(taskItemIndex, 1);
				taskItemIndex = getTaskItemIndex();
			}

			if (pageComponent.heatmapCache.get(changedTask.roleId)) {
				pageComponent.heatmapCache.get(changedTask.roleId).needsRecalculation = true;
			}
			if (changedTask.assignedPersons.length) {
				pageComponent.clearCompanyUtilizationCache();
				for (const personId of changedTask.assignedPersons) {
					if (pageComponent.heatmapCache.get(personId)) {
						pageComponent.heatmapCache.get(personId).needsRecalculation = true;
					}
					const taskItemData = pageComponent.composeTaskItem(changedTask, personId, todayDate);
					if (!taskItemData) continue;
					const taskItem = new TaskItem(taskItemData);
					items.push(taskItem);
				}
			} else {
				const taskItemData = pageComponent.composeTaskItem(changedTask, null, todayDate);
				if (taskItemData) {
					const taskItem = new TaskItem(taskItemData);
					items.push(taskItem);
				}
			}
		}
	} else if (response.deleteDependency) {
		// If we don't plan on showing dependancies on People Schedule, this should be removed along with dependancy data
		const index = data.dependencies.findIndex(
			dependency => dependency.id === response.deleteDependency.deletedDependencyId
		);
		if (index >= 0) {
			data.dependencies.splice(index, 1);
		}
	} else if (response.createDependency) {
		// If we don't plan on showing dependancies on People Schedule, this should be removed along with dependancy data
		if (response.createDependency.errors) return;
		const responseDependency = response.createDependency.dependency.node;
		data.dependencies.push({
			id: responseDependency.id,
			thisDependsOnTaskId: responseDependency.thisDependsOnTask.id,
			taskIdDependsOnThis: responseDependency.taskDependsOnThis.id,
			type: responseDependency.type,
		});
	} else if (response.updateDependency) {
		// If we don't plan on showing dependancies on People Schedule, this should be removed along with dependancy data
		if (response.updateDependency.errors) return;
		const dependency = data.dependencies.find(dependency => dependency.id === response.updateDependency.dependency.id);
		if (!dependency) return;
		const responseDependency = response.createDependency.dependency.node;
		dependency.type = responseDependency.type;
		dependency.thisDependsOnTaskId = responseDependency.thisDependsOnTask.id;
		dependency.taskIdDependsOnThis = responseDependency.taskDependsOnThis.id;
	} else if (response.createClient) {
		const client = response.createClient.client.node;
		data.clients.push({
			id: client.id,
			name: client.name,
		});
	} else if (response.createMultipleAllocations) {
		const project = response.createMultipleAllocations.project || response.createMultipleAllocations.projectGroup;
		if (project?.allocations?.edges) {
			for (const allocationEdge of project.allocations.edges) {
				createOrUpdateAllocation(allocationEdge.node);
			}
			pageComponent.clearCompanyUtilizationCache();
		}
	} else if (response.createProject || response.duplicateProject) {
		const project = response.duplicateProject
			? response.duplicateProject.project.node
			: response.createProject.project.node;
		const alreadyExists = data.projects.find(dataProject => dataProject.id === project.id); //We get the response twice when creating project allocation project, ensure not to add things twice

		if (alreadyExists) return;

		const formattedProject = {
			id: project.id,
			status: project.status,
			companyProjectId: project.companyProjectId,
			name: project.name,
			projectColor: project.projectColor,
			useManualAllocations: project.useManualAllocations,
			completion: project.completion,
			remaining: project.remaining,
			projectStartDay: project.projectStartDay,
			projectStartMonth: project.projectStartMonth,
			projectStartYear: project.projectStartYear,
			projectEndDay: project.projectEndDay,
			projectEndMonth: project.projectEndMonth,
			projectEndYear: project.projectEndYear,
			estimationUnit: project.estimationUnit,
			minutesPerEstimationPoint: project.minutesPerEstimationPoint,
			isInProjectGroup: project.isInProjectGroup,
			projectGroupId: project.projectGroupId,
			forecast: project.forecast,
			clientId: project.client ? project.client.id : null,
			fullAccessToProject: true,
		};
		data.projects.push(formattedProject);
		const newProjectStatus = {
			color: '#33cc33',
			projectId: project.id,
		};
		data.projectMap.set(project.id, formattedProject);
		data.projectStatuses.push(newProjectStatus);
		data.projectStatusMap.set(project.id, newProjectStatus);

		if (project.projectLabels) {
			for (const projectLabelEdge of project.projectLabels.edges) {
				const projectLabel = projectLabelEdge.node;
				const newProjectLabel = {
					id: projectLabel.id,
					labelId: projectLabel.label.id,
					projectId: project.id,
				};
				data.projectLabels.push(newProjectLabel);
				const projectLabelsOnProject = data.projectLabelByProjectMap[project.id] || [];
				data.projectLabelByProjectMap[project.id] = projectLabelsOnProject;
				projectLabelsOnProject.push(newProjectLabel);
			}
		}

		if (project.tasks) {
			for (const taskEdge of project.tasks.edges) {
				const task = taskEdge.node;
				const taskData = {
					projectId: project.id,
					approved: true,
					assignedPersons: task.assignedPersons.map(assignedPerson => {
						return assignedPerson.id;
					}),
					billable: task.billable,
					blocked: task.blocked,
					bug: task.bug,
					companyTaskId: task.companyTaskId,
					deadlineDay: task.deadlineDay,
					deadlineMonth: task.deadlineMonth,
					deadlineYear: task.deadlineYear,
					deadlineFrom: task.deadlineFrom,
					startFrom: task.startFrom,
					done: task.done,
					estimateForecast: task.estimateForecast,
					highPriority: task.highPriority,
					id: task.id,
					taskLabels: task.taskLabels.map(t => ({labelId: t.label.id})),
					roleId: task.role ? task.role.id : undefined,
					phaseId: task.phase ? task.phase.id : undefined,
					startDay: task.startDay,
					startMonth: task.startMonth,
					startYear: task.startYear,
					timeLeft: task.timeLeft,
					name: task.name,
					fullAccessToProject: true,
				};
				data.tasks.push(taskData);
				data.taskMap.set(taskData.id, taskData);
			}
		}

		if (project.phases) {
			for (const phaseEdge of project.phases.edges) {
				const phase = phaseEdge.node;

				data.phases.push({
					id: phase.id,
					deadlineDay: phase.deadlineDay,
					deadlineMonth: phase.deadlineMonth,
					deadlineYear: phase.deadlineYear,
					startDay: phase.startDay,
					startMonth: phase.startMonth,
					startYear: phase.startYear,
					name: phase.name,
					projectId: project.id,
				});
			}
		}

		if (project.projectPersons) {
			for (const projectPersonEdge of project.projectPersons.edges) {
				createProjectPerson(project.id, projectPersonEdge.node);
			}
		}
	} else if (response.createPerson) {
		const person = response.createPerson.person.node;
		const personSubGroups = [];
		personSubGroups.push(
			new NonProjectTimeGroup({
				id: person.id + '-2',
				personId: person.id,
				name: formatMessage({id: 'common.non-project-time'}),
				hideIfEmpty: true,
				hideCondition: () => {
					return pageComponent.isProjectGroupingDisabled();
				},
				onItemCreate: !hasPermission(PERMISSION_TYPE.ALLOCATION_CREATE)
					? undefined
					: (startDate, endDate) => {
							const momentStartDate = getMomentFromCanvasTimelineDate(startDate);
							const momentEndDate = getMomentFromCanvasTimelineDate(endDate);
							showModal({
								type: MODAL_TYPE.CANVAS_CREATE,
								projectGroups: data.projectGroups,
								projects: data.projects,
								idleTimes: data.idleTimes,
								projectPersons: data.projectPersons,
								actualPersonId: data.viewer.actualPersonId,
								persons: data.persons,
								selectedPersonId: person.id,
								milestones: data.phases,
								company: data.company,
								holidayCalendars: data.holidayCalendars,
								holidayCalendarEntries: data.holidayCalendarEntries,
								allocation: {
									personId: person.id,
									startDay: momentStartDate.date(),
									startMonth: momentStartDate.month() + 1,
									startYear: momentStartDate.year(),
									endDay: momentEndDate.date(),
									endMonth: momentEndDate.month() + 1,
									endYear: momentEndDate.year(),
									monday: person.monday,
									tuesday: person.tuesday,
									wednesday: person.wednesday,
									thursday: person.thursday,
									friday: person.friday,
									saturday: person.saturday,
									sunday: person.sunday,
									registerTime: false,
								},
								roles: data.roles,
								teams: data.teams,
								teamPersons: data.teamPersons,
								isCreatingInNonProjectTime: true,
								removeLowHigh: data.viewer.availableFeatureFlags.some(flag => flag.key === 'remove_low_high'),
							});
					  },
			})
		);
		personSubGroups.push(
			new PlaceholderGroup({
				id: person.id + '-3',
				name: formatMessage({id: 'scheduling.no_current_projects'}),
				tasksLeftText: formatMessage({id: 'common.tasks_left'}),
				showOnlyIfNoSiblingsVisible: true,
				personId: person.id,
				onMouseEnter: pageComponent.onTaskGroupMouseEnter.bind(pageComponent, `${person.id}-3`, false, true, false),
				onMouseLeave: pageComponent.onTaskGroupMouseLeave.bind(pageComponent),
				onItemCreate:
					data.company.isUsingProjectAllocation && !hasPermission(PERMISSION_TYPE.ALLOCATION_CREATE)
						? undefined
						: (startDate, endDate) => {
								const momentStartDate = getMomentFromCanvasTimelineDate(startDate);
								const momentEndDate = getMomentFromCanvasTimelineDate(endDate);
								showModal({
									type: MODAL_TYPE.CANVAS_CREATE,
									projectGroups: data.projectGroups,
									projects: data.projects,
									idleTimes: data.idleTimes,
									projectPersons: data.projectPersons,
									actualPersonId: data.viewer.actualPersonId,
									persons: data.persons,
									selectedPersonId: person.id,
									milestones: data.phases,
									company: data.company,
									holidayCalendars: data.holidayCalendars,
									holidayCalendarEntries: data.holidayCalendarEntries,
									allocation: {
										personId: person.id,
										startDay: momentStartDate.date(),
										startMonth: momentStartDate.month() + 1,
										startYear: momentStartDate.year(),
										endDay: momentEndDate.date(),
										endMonth: momentEndDate.month() + 1,
										endYear: momentEndDate.year(),
										monday: person.monday,
										tuesday: person.tuesday,
										wednesday: person.wednesday,
										thursday: person.thursday,
										friday: person.friday,
										saturday: person.saturday,
										sunday: person.sunday,
										registerTime: false,
									},
									roles: data.roles,
									teams: data.teams,
									teamPersons: data.teamPersons,
									removeLowHigh: data.viewer.availableFeatureFlags.some(
										flag => flag.key === 'remove_low_high'
									),
								});
						  },
			})
		);
		const personGroup = new PersonGroup({
			id: person.id,
			name: `${person.firstName == null ? '' : person.firstName} ${person.lastName == null ? '' : person.lastName}`,
			startDate: person.startDate,
			endDate: person.endDate,
			roleId: person.role ? person.role.id : null,
			roleName: person.role ? person.role.name : null,
			profilePictureId: person.profilePictureId,
			profilePictureDefaultId: person.profilePictureDefaultId,
			groups: personSubGroups,
			composeItems: pageComponent.composePersonHeatMapRow.bind(pageComponent),
			filterGroup: pageComponent.filterPersonGroupV4.bind(pageComponent),
			labelIds: [],
			skillIds: [],
			monday: person.monday,
			tuesday: person.tuesday,
			wednesday: person.wednesday,
			thursday: person.thursday,
			friday: person.friday,
			saturday: person.saturday,
			sunday: person.sunday,
			holidayCalendarId: null,
			onPersonImageLoad: pageComponent.onPersonImageLoad.bind(pageComponent),
			isHeatMapDisabled: pageComponent.isHeatMapDisabled.bind(pageComponent),
			onMouseEnter: pageComponent.onPersonGroupMouseEnter.bind(pageComponent, person),
			onMouseLeave: pageComponent.onPersonGroupMouseLeave.bind(pageComponent, person),
		});
		pageComponent.state.groups.splice(-1, 0, personGroup);
		data.persons.push({
			id: person.id,
			firstName: person.firstName,
			lastName: person.lastName,
			monday: person.monday,
			tuesday: person.tuesday,
			wednesday: person.wednesday,
			thursday: person.thursday,
			friday: person.friday,
			saturday: person.saturday,
			sunday: person.sunday,
			profilePictureDefaultId: person.profilePictureDefaultId,
			profilePictureId: null,
			roleId: person.role ? person.role.id : null,
			active: true,
			personLabels: [],
		});
	}
	pageComponent.forceUpdate();
	if (!response.storeSchedulePerformance) {
		dispatch(EVENT_ID.CANVAS_TIMELINE_FORCE_REDRAW, {preventFiltering: false});
		requestAnimationFrame(() => {
			dispatch(EVENT_ID.CANVAS_TIMELINE_FORCE_REDRAW, {preventFiltering: false});
		});
	}
};
